import{useCallback,useMemo,useState}from"react";
import{deviceAuthorityApi,securityApi}from"../../lib/api";
import{useResource}from"../../lib/useResource";
import{useAppI18n}from"../../lib/appI18n";
import{useOsText}from"../../components/os/messages";
import{EmptyState,InlineNotice,LoadingLine}from"../../components/State";
import{ResourceList,SectionHeader,StateText,WorkspaceHeader}from"../../components/Foundation";
import{runPrfCeremony}from"../../lib/webauthn";
import{
  usePasskeyType,
  useRecoveryClassLabel,
  useRpLabel,
  useStatusLabel,
  useTrustStateLabel,
}from"./securityTerminology";
import{
  actionAvailability,
  boundKind,
  classifyPasskeyError,
  devicePosture,
  displayName,
  findPasskey,
  isValidCredentialId,
  relyingPartyKind,
  safeCredentialReference,
  safeTransports,
  statusTone,
  trustStateTone,
  validateRename,
}from"./passkeyDetailModel";

type Translator=(key:string)=>string;

function Protected({
  error,
  t,
  onUnlocked,
}:{
  error:string|null;
  t:Translator;
  onUnlocked:()=>unknown;
}){
  const[busy,setBusy]=useState(false);
  const[actionError,setActionError]=useState<string|null>(null);

  if(!error)return null;

  const unlock=async()=>{
    setBusy(true);
    setActionError(null);
    try{
      const options=await deviceAuthorityApi.stepUpOptions();
      await runPrfCeremony(options.ceremony_id,options.publicKey);
      await onUnlocked();
    }catch(e){
      setActionError(e instanceof Error?e.message:t("sc.renameFailed"));
    }finally{
      setBusy(false);
    }
  };

  return <InlineNotice
    tone="attention"
    title={t("sc.authorityRequiredTitle")}
    detail={actionError||t("sc.authorityRequiredDetail")}
    action={
      <button
        type="button"
        className="button secondary pk-action"
        disabled={busy}
        onClick={()=>void unlock()}
      >
        {busy?t("sc.waitingForPasskey"):t("sc.unlockWithPasskey")}
      </button>
    }
  />;
}

function formatDate(value:string|Date|null|undefined){
  if(!value)return"—";
  const date=value instanceof Date?value:new Date(value);
  if(Number.isNaN(date.getTime()))return String(value);
  return new Intl.DateTimeFormat(undefined,{dateStyle:"medium",timeStyle:"short"}).format(date);
}

export function PasskeyDetail({
  credentialUuid,
  onBack,
  onOpenPerson,
}:{
  credentialUuid:string;
  onBack:()=>void;
  onOpenPerson:(subjectId:string)=>void;
}){
  const i18n=useAppI18n();
  const t=useCallback((k:string):string=>i18n.t(k),[i18n]);
  const statusLabel=useStatusLabel();
  const passkeyType=usePasskeyType();
  const rpLabel=useRpLabel();
  const trustLabel=useTrustStateLabel();
  const recoveryClassLabel=useRecoveryClassLabel();
  const text=useOsText();

  const[renameOpen,setRenameOpen]=useState(false);
  const[label,setLabel]=useState("");
  const[reason,setReason]=useState("");
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState<{tone:"ok"|"error";text:string}|null>(null);

  const credentialIdValid=isValidCredentialId(credentialUuid);

  const load=useCallback(async()=>{
    // A malformed reference is a navigation mistake: no request is issued.
    if(!isValidCredentialId(credentialUuid))throw new Error("credential_reference_invalid");
    return securityApi.passkeys();
  },[credentialUuid]);

  const r=useResource(load);

  const passkey=useMemo(
    ()=>findPasskey(r.data?.passkeys,credentialUuid),
    [r.data,credentialUuid],
  );

  const refresh=useCallback(()=>{void r.refresh();},[r]);

  const error=r.status==="error"&&!r.data?r.error:null;
  const failure=classifyPasskeyError(error);
  const notFound=!error&&!passkey&&r.status==="success";

  const posture=useMemo(()=>devicePosture(passkey),[passkey]);
  const transports=useMemo(()=>safeTransports(passkey?.transports),[passkey]);
  const actions=useMemo(()=>actionAvailability(passkey),[passkey]);
  const rpKind=useMemo(
    ()=>relyingPartyKind(passkey?.rp_profile_id,passkey?.rp_kind),
    [passkey],
  );
  const credentialRef=safeCredentialReference(passkey?.credential_uuid);
  const bound=passkey?boundKind(passkey):"device-bound";

  const startRename=()=>{
    setLabel(displayName(passkey!));
    setReason("");
    setNotice(null);
    setRenameOpen(true);
  };

  const submitRename=async()=>{
    if(busy||!passkey)return;
    const check=validateRename({label,reason});
    if(!check.ok){
      setNotice({tone:"error",text:t("sc.renameFailed")});
      return;
    }
    setBusy(true);
    setNotice(null);
    try{
      // Destructive and administrative actions always require a fresh
      // passkey / PRF step-up ceremony before the request is issued.
      const options=await deviceAuthorityApi.stepUpOptions();
      const proof=await runPrfCeremony(options.ceremony_id,options.publicKey);
      await securityApi.renamePasskey(
        passkey.credential_uuid,
        check.label,
        check.reason,
        proof.ceremony_id,
        proof.credential,
        proof.prf_result,
      );
      setRenameOpen(false);
      setNotice({tone:"ok",text:t("sc.renamedSuccess")});
      await refresh();
    }catch{
      setNotice({tone:"error",text:t("sc.renameFailed")});
    }finally{
      setBusy(false);
    }
  };

  const backButton=(
    <button type="button" className="person-back" onClick={onBack}>
      <span aria-hidden="true">&#8592;</span>
      <span>{t("sc.backToDevices")}</span>
    </button>
  );

  if(!credentialIdValid){
    return <div className="page person-detail">
      {backButton}
      <EmptyState title={t("sc.invalidCredentialId")} detail={t("sc.invalidCredentialIdDesc")}/>
    </div>;
  }

  return <div className="page person-detail passkey-detail">
    <WorkspaceHeader
      eyebrow={t("sc.passkeyDetail")}
      title={passkey?displayName(passkey):t("sc.passkeyTypeUnknown")}
      description={passkey?passkeyType(passkey.device_type,passkey.backed_up):undefined}
      actions={
        <>
          {backButton}
          {r.status==="loading"?<LoadingLine label={t("sc.passkeyDetail")}/>:null}
        </>
      }
    />

    {notice&&(
      <div className={`pk-notice ${notice.tone}`} role="status">{notice.text}</div>
    )}

    {notFound&&(
      <EmptyState
        title={t("sc.passkeyNotFound")}
        detail={t("sc.passkeyNotFoundDesc")}
        action={backButton}
      />
    )}

    {error&&failure!=="guarded"&&(
      <EmptyState
        title={t("sc.passkeyLoadFailed")}
        detail={t("sc.personLoadFailedDesc")}
        action={
          <button type="button" className="button secondary pk-action" onClick={refresh}>
            {t("sc.retry")}
          </button>
        }
      />
    )}

    <Protected error={failure==="guarded"?error:null} t={t} onUnlocked={refresh}/>

    {r.status==="loading"&&!r.data?<LoadingLine label={t("sc.passkeyDetail")}/>:null}

    {passkey&&<>
      <div className="person-grid">
        <section className="person-card">
          <h3>{t("sc.personSummary")}</h3>
          <dl className="person-facts">
            <div>
              <dt>{t("sc.personSummary")}</dt>
              <dd>
                <button
                  type="button"
                  className="pk-link"
                  onClick={()=>onOpenPerson(passkey.subject_id)}
                >
                  {passkey.subject_name}
                </button>
              </dd>
            </div>
            <div><dt>{t("sc.status")}</dt><dd><StateText tone={statusTone(passkey.status)}>{statusLabel(passkey.status)}</StateText></dd></div>
            <div><dt>{t("sc.createdAt")}</dt><dd>{formatDate(passkey.created_at)}</dd></div>
            <div><dt>{t("sc.lastUsed")}</dt><dd>{formatDate(passkey.last_used_at)}</dd></div>
            <div><dt>{t("sc.credentialOrigin")}</dt><dd>{passkey.enrolled_by_name||"—"}</dd></div>
          </dl>
        </section>

        <section className="person-card">
          <h3>{t("sc.credentialInfo")}</h3>
          <dl className="person-facts">
            <div><dt>{t("sc.passkeyTypeUnknown")}</dt><dd>{passkeyType(passkey.device_type,passkey.backed_up)}</dd></div>
            <div><dt>{t("sc.synced")}</dt><dd><StateText tone={passkey.backed_up?"positive":"neutral"}>{bound==="synced"?t("sc.synced"):t("sc.deviceBound")}</StateText></dd></div>
            <div><dt>{t("sc.backedUp")}</dt><dd><StateText tone={passkey.backed_up?"positive":"attention"}>{passkey.backed_up?t("sc.yesShort"):t("sc.noShort")}</StateText></dd></div>
            <div><dt>{t("sc.unlockCapable")}</dt><dd><StateText tone={passkey.unlock_capable?"positive":"neutral"}>{passkey.unlock_capable?t("sc.yesShort"):t("sc.noShort")}</StateText></dd></div>
            <div><dt>{t("sc.prfCapableShort")}</dt><dd><StateText tone={passkey.prf_capable?"positive":"neutral"}>{passkey.prf_capable?t("sc.yesShort"):t("sc.noShort")}</StateText></dd></div>
            <div><dt>{t("sc.trustGroups")}</dt><dd><code>{passkey.trust_group||"—"}</code></dd></div>
            <div><dt>{t("sc.rpProfile")}</dt><dd>
              <StateText tone={rpKind==="current"?"positive":rpKind==="legacy"?"attention":"neutral"}>
                {rpLabel(passkey.rp_kind)}
              </StateText>
            </dd></div>
            {passkey.rp_name&&<div><dt>{t("sc.rpName")}</dt><dd>{passkey.rp_name}</dd></div>}
          </dl>
        </section>

        <section className="person-card">
          <h3>{t("sc.devicePostureTitle")}</h3>
          {posture.present?
            <>
              <p className="person-copy">{t("sc.devicePostureDesc")}</p>
              <dl className="person-facts">
                <div><dt>{t("sc.deviceLabel")}</dt><dd>{posture.deviceLabel||"—"}</dd></div>
                <div><dt>{t("sc.platform")}</dt><dd>{posture.platform||"—"}</dd></div>
                <div><dt>{t("sc.trustState")}</dt><dd><StateText tone={trustStateTone(posture.trustState)}>{trustLabel(posture.trustState)}</StateText></dd></div>
                <div><dt>{t("sc.recoveryClass")}</dt><dd>{recoveryClassLabel(posture.recoveryClass)}</dd></div>
                <div><dt>{t("sc.postureLastSeen")}</dt><dd>{formatDate(posture.lastSeenAt)}</dd></div>
                <div><dt>{t("sc.lastVerified")}</dt><dd>{formatDate(posture.lastVerifiedAt)}</dd></div>
              </dl>
            </>
            :<EmptyState title={t("sc.noDevicePosture")} detail={t("sc.noDevicePostureDesc")}/>}
        </section>
      </div>

      <section className="person-section">
        <SectionHeader title={t("sc.credentialInfo")} description={t("sc.credentialInfoDesc")}/>
        <details className="os-technical">
          <summary>{text("technical")}</summary>
          <div className="os-technical-body">
            <ResourceList label={t("sc.credentialInfo")}>
              <dl className="person-facts">
                {credentialRef&&<div><dt>{t("sc.credentialRef")}</dt><dd><code>{credentialRef}</code></dd></div>}
                <div><dt>{t("sc.aaguid")}</dt><dd><code>{passkey.aaguid||"—"}</code></dd></div>
                <div><dt>{t("sc.transports")}</dt><dd>{transports.length?transports.join(", "):"—"}</dd></div>
                <div><dt>{t("sc.rpId")}</dt><dd><code>{passkey.rp_id||"—"}</code></dd></div>
                <div><dt>{t("sc.signCount")}</dt><dd>{passkey.sign_count}</dd></div>
              </dl>
            </ResourceList>
          </div>
        </details>
      </section>

      <section className="person-section">
        <SectionHeader title={t("sc.renamePasskey")} description={t("sc.renamePasskeyHelp")}/>

        {!renameOpen&&(
          <div className="pk-actions">
            <button
              type="button"
              className="button primary pk-action"
              disabled={!actions.rename||busy}
              onClick={startRename}
            >
              {t("sc.renamePasskey")}
            </button>
          </div>
        )}

        {renameOpen&&(
          <form
            className="pk-form"
            onSubmit={e=>{e.preventDefault();void submitRename();}}
          >
            <p className="person-copy">{t("sc.renameRequiresStepUp")}</p>
            <label className="pk-field">
              <span>{t("sc.newName")}</span>
              <input
                type="text"
                value={label}
                maxLength={255}
                required
                onChange={e=>setLabel(e.target.value)}
              />
            </label>
            <label className="pk-field">
              <span>{t("sc.changeReason")}</span>
              <input
                type="text"
                value={reason}
                required
                onChange={e=>setReason(e.target.value)}
              />
              <small>{t("sc.changeReasonHint")}</small>
            </label>
            <div className="pk-actions">
              <button type="submit" className="button primary pk-action" disabled={busy}>
                {busy?t("sc.waitingForPasskey"):t("sc.confirmRename")}
              </button>
              <button
                type="button"
                className="button secondary pk-action"
                disabled={busy}
                onClick={()=>setRenameOpen(false)}
              >
                {text("close")}
              </button>
            </div>
          </form>
        )}

        {!actions.revoke&&(
          <InlineNotice
            tone="neutral"
            title={t("sc.revokeSelfOnly")}
            detail={t("sc.revokeSelfOnlyDesc")}
          />
        )}

        {!actions.lostDevice&&(
          <InlineNotice
            tone="neutral"
            title={t("sc.lostDeviceUnsupported")}
            detail={t("sc.lostDeviceUnsupportedDesc")}
          />
        )}
      </section>
    </>}
  </div>;
}